import React from 'react';
import { DateTime } from 'luxon';
import { PickersDayProps } from '@mui/x-date-pickers';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { Box } from '@mui/material';
import { Theme } from '@routific/hive';
import styled from 'styled-components';
import StaticDay from './StaticDay';
import { DatePickerType } from '../DatePicker';
import { getNewRangeEnd, isWithinRange, RANGE_BACKGROUND_COLOR } from './helpers/dayHelpers';
import { observer } from 'mobx-react';

const StyledDayContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: '50%',
    top: '19%',
    color: 'white',
    height: '4px',
    minWidth: '4px',
  },
}));

interface DayProps extends PickersDayProps<any> {
  rangeStart?: DateTime | null,
  rangeEnd?: DateTime | null,
  hoveredDay?: DateTime | null,
  selectedDay?: DateTime | null,
  rangeType?: DatePickerType,
  dayHasRoutes?: (dayToCheck: any) => boolean,
}

const Day: React.FC<DayProps> = ({
  day, rangeStart, rangeEnd, hoveredDay, selectedDay, rangeType, className, disabled, dayHasRoutes, ...props
}) => {
  const hasRange = !!rangeType;
  const isHovered = hoveredDay && day.hasSame(hoveredDay, 'day');

  // Update range start or end based on hovered interaction
  const newRangeStart = rangeType === DatePickerType.START && hoveredDay ? hoveredDay : rangeStart;
  const newRangeEnd = getNewRangeEnd(rangeType, hoveredDay, rangeEnd);
  const isStart = newRangeStart && day.hasSame(newRangeStart, 'day');
  const isEnd = newRangeEnd && day.hasSame(newRangeEnd, 'day');
  const isSelected = selectedDay && day.hasSame(selectedDay, 'day');

  const isInRange = isWithinRange(day, newRangeStart, newRangeEnd);

  // Range box specific classes
  const rangeBoxClassNames = [
    'HiveRangeBox',
    isStart && 'HiveRangeBox-rangeStart',
    isEnd && 'HiveRangeBox-rangeEnd',
    isInRange && 'HiveRangeBox-inRange',
    disabled && 'HiveRangeBox-disabled',
  ]
    .filter(Boolean)
    .join(' ');

  const getBadgeColor = () => {
    if (props.outsideCurrentMonth || disabled) {
      return Theme.Colors.primary3;
    }

    return isSelected || isStart || isEnd ? Theme.Colors.gray0 : Theme.Colors.primary5;
  };

  const getStaticDay = () => {
    const staticDay =
      <StaticDay
        isSelected={isSelected}
        isBookend={isStart || isEnd}
        hasRange={hasRange}
        isHovered={isHovered}
        day={day}
        className={className}
        disabled={disabled}
        {...props} />;
    if (dayHasRoutes && dayHasRoutes(day)) {
      return (
        <StyledBadge data-testid={'dot_badge'} variant='dot' sx={{
          '& .MuiBadge-badge': {
            backgroundColor: getBadgeColor(),
          },
        }}>
          {staticDay}
        </StyledBadge>
      );
    }

    return staticDay;
  };

  return (
    <Box
      onMouseEnter={() => props.onPointerEnter?.(day)}
      onMouseLeave={() => props.onPointerLeave?.(day)}
      className={rangeBoxClassNames}
      sx={{
        '&.HiveRangeBox': {
          padding: `0 ${Theme.Spacing.xsSpacing}`,
          margin: '2px 0',
        },
        '&.HiveRangeBox-inRange:not(.HiveRangeBox-disabled)': {
          backgroundColor: RANGE_BACKGROUND_COLOR,
          '&.HiveRangeBox-rangeStart:not(.HiveRangeBox-rangeEnd)': {
            borderRadius: '50% 0 0 50%',
            backgroundImage: `linear-gradient(90deg, white 50%, ${RANGE_BACKGROUND_COLOR} 50%)`,
          },
          '&.HiveRangeBox-rangeEnd:not(.HiveRangeBox-rangeStart)': {
            borderRadius: '0 8px 8px 0',
            backgroundImage: `linear-gradient(90deg, ${RANGE_BACKGROUND_COLOR} 50%, white 50%)`,
          },
        },
        '&.HiveRangeBox-inRange.HiveRangeBox-rangeStart.HiveRangeBox-rangeEnd': {
          backgroundImage: 'none',
          backgroundColor: 'white',
        },
      }}
    >
      <StyledDayContainer>
        {getStaticDay()}
      </StyledDayContainer>
    </Box>
  );
};

export default observer(Day);
